import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ShopService } from '@app/services/shops/shop.service';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { Subject, combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ConditionsRules } from './configs/promise-ai-conditions';
import { PromiseAIService, PromiseAISettings } from './promise-ai.service';

import { ROUTE_URLS, SUPPORT_URLS } from '../constants';
import { validateDateVariables } from './promise-ai.utils';

@Component({
  selector: 'app-promise-ai',
  templateUrl: './promise-ai.component.html',
  styleUrls: ['./promise-ai.component.scss'],
  standalone: false,
})
export class PromiseAIComponent implements OnInit, OnDestroy {
  formGroup: UntypedFormGroup;
  tagInstructionsUrl = SUPPORT_URLS.PRODUCT_PAGE.TAG_INSTRUCTIONS;
  blockInstructionsUrl = SUPPORT_URLS.PRODUCT_PAGE.BLOCK_INSTRUCTIONS;
  saving: boolean = false;
  tagCodeControl = new UntypedFormControl({ value: '<PDQPromiseAI></PDQPromiseAI>', disabled: true });
  shop: any;
  destroy$ = new Subject();
  themeEditorUrl: string;
  testModeExample = 'https://storeName.myshopify.com/productName?testProductPromise=1';
  iconPreview: any = '';

  constructor(
    @Inject(PromiseAIService) private promiseAIService: PromiseAIService,
    private toastService: ToastService,
    private shopService: ShopService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private router: Router,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  private initSubscriptions() {
    combineLatest([this.shopService.shop$, this.promiseAIService.promiseAISettings$])
      .pipe(
        filter(([shop, _]) => !!shop),
        takeUntil(this.destroy$),
      )
      .subscribe(([shop, settings]) => {
        this.shop = shop;
        this.themeEditorUrl = `https://${shop.name}/admin/themes/current/editor?template=product`;
        this.initForm(settings);
      });
  }

  private initForm(settings?: PromiseAISettings) {
    this.formGroup = new UntypedFormGroup({
      testMode: new UntypedFormControl(settings?.testMode ?? false),
      descriptionText: new UntypedFormControl(settings?.descriptionText ?? 'Arrives between {min_date} - {max_date}', [
        Validators.required,
        validateDateVariables(),
      ]),
      fallbackText: new UntypedFormControl(settings?.fallbackText ?? 'Delivery date will be calculated at checkout', [
        Validators.required,
      ]),
      conditions: new UntypedFormGroup({
        conditionsRules: this.rulesDynamicFormService.initRulesFormArray(
          settings?.conditions?.conditionsRules ?? [],
          ConditionsRules,
        ),
      }),
    });

    if (settings?.id) {
      this.formGroup.addControl('id', new UntypedFormControl(settings.id));
    }

    this.formGroup.markAsPristine();
  }

  onSave() {
    if (this.formGroup.invalid) return;

    this.saving = true;
    const settings = this.formGroup.getRawValue();

    this.promiseAIService
      .setPromiseAISettings(settings, this.shop)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.toastService.showToast({
            mode: 'success',
            message: 'AI settings saved successfully',
          });
          this.saving = false;
        },
        (error) => {
          console.error('Error saving AI settings:', error);
          this.toastService.showToast({
            mode: 'danger',
            message: 'Failed to save AI settings',
          });
          this.saving = false;
        },
      );
  }

  navigateToCheckoutSettings() {
    this.router.navigate([ROUTE_URLS.CHECKOUT_SETTINGS]);
  }
}
